"use client";

import { toast } from "@kyan/ui";
import { useQuery } from "@tanstack/react-query";

import { useOneClickTradingFeatureFlag } from "#/hooks/featureFlags/useOneClickTradingFeatureFlag";
import { useShowTradeConfirmation } from "#/store/app/portfolio/settings/showTradeConfirmation";

import { isSessionValid } from "../kyan.service";
import { useCreateSession } from "./useCreateSession";
import { useDeleteSession } from "./useDeleteSession";
import { useServerAccount } from "./useServerAccount";

export const useTradingSession = () => {
  const { serverAccount } = useServerAccount();
  const { setShowTradeConfirmation } = useShowTradeConfirmation();

  const { isEnabled } = useOneClickTradingFeatureFlag();

  const {
    data: isTradingSessionEnabled = false,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["isSessionValid", serverAccount],
    queryFn: isSessionValid,
    enabled: Boolean(serverAccount),
  });

  const { mutateAsync: createSession, isPending: isCreatingSession } =
    useCreateSession({
      onError: (error) => {
        toast.error({ title: "Operation Failed", description: error.message });
      },
      onSuccess: () => {
        refetch();
        setShowTradeConfirmation(true);
      },
    });

  const { mutateAsync: deleteSession, isPending: isDeletingSession } =
    useDeleteSession({
      onError: (error) => {
        toast.error({ title: "Operation Failed", description: error.message });
      },
      onSuccess: () => {
        refetch();
      },
    });

  return {
    isTradingSessionEnabled,
    isLoading: isLoading || !serverAccount,
    shouldUseSessionHash:
      isTradingSessionEnabled && !isLoading && Boolean(serverAccount),
    createSession,
    isCreatingSession,
    deleteSession,
    isDeletingSession,
    isOneClickTradingDisabled: !isEnabled,
  };
};
